<template lang="pug">
.pulse365-container
  .pulse365-header
    .header-left
      h2 {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.page_title') }}
    .header-right
      .date-picker-wrapper
        aiq-date-picker(
          v-model="dateRange"
          type="daterange"
          :shortcuts="datePickerOptions.shortcuts"
          :disabled-date="datePickerOptions.disabledDate"
          format="YYYY/MM/DD"
          :clearable="false"
          popper-class="metrics-time-range__date-picker-popup"
          :teleported="false"
          :default-time="getDatePickerDefaultTime()"
          @change="onDateRangeChange"
        )

  .pulse365-content(v-if="pulseConfig")
    //- Total Section
    .metrics-section(v-if="pulseConfig.total_metrics?.length > 0")
      .section-header
        .section-title {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.total_section_title') }}
      .metrics-grid
        MetricCard(
          v-for="metricId in pulseConfig.total_metrics"
          :key="metricId"
          :title="getMetricLabel(metricId)"
          :value="getMetricValue(null, metricId)"
          :metricCategory="getMetricCategory(metricId)"
          :loading="isLoading"
        )

    //- Segment Sections
    .metrics-section(
      v-for="(segment, index) in configuredSegments"
      :key="segment.display_name"
    )
      .section-header
        .section-title {{ segment.display_name }}
      .metrics-grid
        MetricCard(
          v-for="metricId in segment.metrics"
          :key="`${segment.display_name}-${metricId}`"
          :title="getMetricLabel(metricId)"
          :value="getMetricValue(index, metricId)"
          :metricCategory="getMetricCategory(metricId)"
          :loading="isLoading"
        )

    //- Empty State
    .empty-state(v-if="!hasAnyMetrics")
      .empty-icon
        i.el-icon-data-analysis
      p.empty-title {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.empty_state_title') }}
      p.empty-description {{ $t('settings_tab.agent_tab.dashboard.pulse_configuration.empty_state_description') }}
</template>

<script>
import { mapState } from 'vuex';
import get from 'lodash/get';
import dayjs from '@/localPlugins/dayjs';
import { useTimeZone, getDatePickerDefaultTime, unixStampToTzAttachedMoment } from '@/libs';
import { dstDateRangeUtc, createPickerDateRangeOption, getLastQuarterRange } from '@/pages/Metrics/configs/dateRange';
import MetricCard from './components/MetricCard.vue';

const MAX_DATE_RANGE_MONTHS = 12;

const METRIC_CATEGORIES = {
  active_unique_customers: 'count',
  conversations: 'count',
  messages: 'count',
  first_response_time: 'timedelta',
  agent_first_response_time: 'timedelta',
  handle_time: 'timedelta',
  median_handle_time: 'timedelta',
  fully_automated_conversations: 'percent',
  partially_automated_conversations: 'percent',
  feedback_score: 'score',
  sales_leads: 'count', // TODO: needs logs to calculate
  abandonment_rate: 'percent', // TODO: needs logs to calculate
  relationship_length: 'days', // TODO: needs logs to calculate
  primary_customer_retention_rate: 'percent', // TODO: needs logs to calculate
  automation_driven_cost_savings: 'currency', // TODO: add query
  channel_cost_savings: 'currency', // TODO: add query (chat vs phone)
};

export default {
  name: 'Pulse365',
  components: {
    MetricCard,
  },
  data() {
    return {
      dateRange: [
        dayjs().subtract(1, 'month').startOf('day').toDate(),
        dayjs().subtract(1, 'day').endOf('day').toDate(),
      ],
      isLoading: false,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  },
  computed: {
    ...mapState({
      pulseConfig: state => state.settings.pulseConfig,
      metricsData: state => state.pulse.pulse365.data,
    }),
    datePickerOptions() {
      const todayStartInTz = dayjs().tz(this.timezone).startOf('day').valueOf();
      const twelveMonthsAgo = dayjs().tz(this.timezone).subtract(MAX_DATE_RANGE_MONTHS, 'months').startOf('day').valueOf();
      return {
        disabledDate: (time) => time.getTime() >= todayStartInTz || time.getTime() < twelveMonthsAgo,
        shortcuts: [
          { label: 'Last Month', startTime: () => dayjs().subtract(1, 'month').startOf('month'), endTime: () => dayjs().subtract(1, 'month').endOf('month') },
          { label: 'Last Quarter', ...getLastQuarterRange() },
        ].map(createPickerDateRangeOption),
      };
    },
    configuredSegments() {
      return get(this.pulseConfig, 'segments', [])
        .filter(seg => get(seg, 'segment_names', []).length > 0 && get(seg, 'metrics', []).length > 0)
        .map(seg => ({
          segment_names: seg.segment_names,
          display_name: seg.segment_names.join(', '),
          metrics: seg.metrics,
        }));
    },
    hasAnyMetrics() {
      const hasTotalMetrics = (this.pulseConfig?.total_metrics?.length ?? 0) > 0;
      const hasSegmentMetrics = this.configuredSegments.length > 0;
      return hasTotalMetrics || hasSegmentMetrics;
    },
  },
  async mounted() {
    this.timezone = await useTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      this.$store.dispatch,
      this.$store._state.data.configs,
    );
    await this.loadPulseConfig();
    await this.fetchMetrics();
  },
  methods: {
    async loadPulseConfig() {
      try {
        await this.$store.dispatch('settings/getSetting', {
          name: 'pulse_configuration',
          stateName: 'pulseConfig',
        });
      } catch (err) {
        this.$aiq.notify.error('Failed to load pulse configuration');
      }
    },
    getDatePickerDefaultTime,
    async fetchMetrics() {
      if (!this.pulseConfig) return;

      this.isLoading = true;
      try {
        const [rawStart, rawEnd] = dstDateRangeUtc(this.dateRange);
        const params = {
          period_start: unixStampToTzAttachedMoment(rawStart, this.timezone).unix(),
          period_end: unixStampToTzAttachedMoment(rawEnd, this.timezone).unix(),
          time_zone: this.timezone,
        };

        await this.$store.dispatch('pulse/getPulse365Metrics', params);
      } catch (err) {
        this.$aiq.notify.error('Failed to fetch metrics');
      } finally {
        this.isLoading = false;
      }
    },
    onDateRangeChange() {
      return this.fetchMetrics();
    },
    getMetricLabel(metricId) {
      return this.$t(`settings_tab.agent_tab.dashboard.pulse_configuration.metrics.${metricId}`);
    },
    getMetricCategory(metricId) {
      return METRIC_CATEGORIES[metricId] || 'count';
    },
    getMetricValue(segmentIndex, metricId) {
      // For total metrics (segmentIndex is null)
      if (segmentIndex === null) {
        return this.metricsData.total?.[metricId]?.value ?? null;
      }

      // For segment metrics (segmentIndex is the index in segment_groups)
      const segmentGroup = this.metricsData.segment_groups?.[segmentIndex];
      if (!segmentGroup) return null;

      return segmentGroup.metrics?.[metricId]?.value ?? null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../styles/aiq-variables.scss";

.pulse365-container {
  padding: 16px 20px;
  height: calc(100vh - 160px);
  overflow-y: auto;
  background: #f5f7fa;
}

.pulse365-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
}

.header-left {
  h2 {
    font-size: 20px;
    font-weight: 700;
    color: $aiq-color-text-primary;
    margin: 0;
  }
}

.date-picker-wrapper {
  min-width: 260px;
  position: relative;
}

.pulse365-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.metrics-section {
  background: #fff;
  border-radius: 8px;
  padding: 14px 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.section-header {
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #ebeef5;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: $aiq-color-text-primary;
  margin-bottom: 0;
}

.metrics-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  ::v-deep .metric-card {
    flex: 1 1 140px;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  background: #fff;
  border-radius: 16px;
  padding: 40px;
  text-align: center;

  .empty-icon {
    font-size: 48px;
    color: #dcdfe6;
    margin-bottom: 16px;
  }

  .empty-title {
    font-size: 18px;
    font-weight: 600;
    color: $aiq-color-text-primary;
    margin-bottom: 8px;
  }

  .empty-description {
    font-size: 14px;
    color: $aiq-color-text-muted;
    max-width: 400px;
  }
}

</style>

<style lang="scss">
@import "../../../styles/aiq-variables-dark.scss";

[data-theme="dark"] .pulse365-container {
  background: $aiq-bgc-body;

  .pulse365-header h2 {
    color: $aiq-color-black;
  }

  .metrics-section {
    background: $aiq-bgc-sidebar-item-hover;
    box-shadow: none;
    border: 1px solid $aiq-bdc-input-default;

    .section-header {
      border-bottom-color: $aiq-bdc-input-default;
    }

    .section-title {
      color: $aiq-color-black;
    }

  }

  .empty-state {
    background: $aiq-bgc-sidebar-item-hover;
    border: 1px solid $aiq-bdc-input-default;

    .empty-title {
      color: $aiq-color-black;
    }

    p {
      color: $aiq-color-text-muted;
    }
  }
}
</style>
